import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Lightning, Timer } from "@phosphor-icons/react";
import { ConsultNowSound } from "@/components/ConsultNowSound";

const DUR_LABEL = { 30: "30 menit", 60: "1 jam", 120: "2 jam" };

const ST = { available: "Siap menerima konsultasi", held: "Ditahan untuk pasien", busy: "Sedang konsultasi", offline: "Offline" };
const left = (ts, skew) => Math.max(0, Math.round((ts || 0) - (Date.now() / 1000 + skew)));
const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export const ConsultNowPanel = () => {
  const [me, setMe] = useState(null);
  const [skew, setSkew] = useState(0);
  const [, tick] = useState(0);
  const nav = useNavigate();
  const apply = (d) => { setMe(d); setSkew(d.server_ts - Date.now() / 1000); };
  const load = useCallback(() => { api.get("/consult-now/me").then((r) => apply(r.data)).catch(() => setMe(false)); }, []);
  const setStatus = async (status, duration_min) => {
    try { apply((await api.post("/consult-now/presence", { status, duration_min })).data); } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, [load]);
  useEffect(() => { const id = setInterval(() => tick((x) => x + 1), 1000); return () => clearInterval(id); }, []);
  if (!me) return null;
  const cur = me.current;
  const accept = async () => {
    try { const r = await api.post(`/consult-now/${cur.reservation_id}/accept`); toast.success("Sesi dimulai"); nav(`/konsultasi/${r.data.room_id}`); }
    catch (e) { toast.error(apiError(e)); load(); }
  };
  return (
    <div data-testid="consult-now-panel" className="mb-4 rounded-2xl border border-amber-200 bg-amber-50/60 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-sm font-bold text-amber-800"><Lightning size={16} weight="fill" /> Konsultasi Sekarang: <span data-testid="consult-now-status">{ST[me.status]}</span></p>
        {me.status !== "busy" && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-semibold text-emerald-800">{me.just_finished ? "Kembali tersedia:" : "Siap:"}</span>
            {(me.durations || [30, 60, 120]).map((d) => (
              <button key={d} data-testid={`consult-now-available-${d}`} onClick={() => setStatus("available", d)} className={`rounded-full px-3 py-1 text-xs font-bold ${me.status === "available" && me.duration_min === d ? "bg-emerald-600 text-white" : "bg-white text-emerald-700 ring-1 ring-emerald-200"}`}>{DUR_LABEL[d] || `${d} menit`}</button>
            ))}
            <button data-testid="consult-now-offline" onClick={() => setStatus("offline")} className={`rounded-full px-3 py-1 text-xs font-bold ${me.status === "offline" ? "bg-slate-700 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`}>Offline</button>
          </div>
        )}
      </div>
      {me.status === "available" && <p className="mt-1 text-[11px] text-amber-700" data-testid="consult-now-ttl">Siap {DUR_LABEL[me.duration_min] || ""} — tetap Siap walau halaman ditutup, berakhir otomatis dalam {mmss(left(me.expires_ts, skew))} atau saat Anda menekan Offline. Tampil untuk pasien hanya bila tidak bentrok jadwal/blokir dan konsultasi selesai sebelum 00.00 WITA.</p>}
      {me.status === "offline" && me.auto_offline === "no_response" && <p className="mt-1 text-[11px] font-semibold text-rose-700" data-testid="consult-now-auto-offline">Status otomatis Offline karena pesanan tidak diterima dalam 5 menit. Pasien ditangani Admin GBS.</p>}
      {me.just_finished && me.status === "offline" && <p className="mt-1 text-[11px] text-slate-600" data-testid="consult-now-finished">Sesi selesai. Pilih “Kembali tersedia” atau tetap Offline.</p>}
      {cur && (
        <div className="mt-2 rounded-xl bg-white p-2.5 text-xs" data-testid="consult-now-current">
          <p className="font-bold text-slate-700">{cur.pasien} · {cur.layanan}</p>
          {cur.phase === "hold" && <p className="mt-0.5 text-slate-500" data-testid="consult-now-hold">Menunggu pembayaran pasien · <Timer size={12} className="inline" /> {mmss(left(cur.pay_deadline_ts, skew))}</p>}
          {cur.phase === "await_accept" && (
            <div className="mt-1 flex items-center justify-between gap-2">
              <span className="font-semibold text-rose-600" data-testid="consult-now-accept-countdown">Lunas — terima dalam {mmss(left(cur.accept_deadline_ts, skew))}</span>
              <button data-testid="consult-now-accept" onClick={accept} className="rounded-full bg-emerald-600 px-3 py-1 font-bold text-white">Terima</button>
            </div>
          )}
          {cur.phase === "active" && cur.room_id && <button data-testid="consult-now-open-room" onClick={() => nav(`/konsultasi/${cur.room_id}`)} className="mt-1 rounded-full bg-teal-600 px-3 py-1 font-bold text-white">Buka Ruang Konsultasi</button>}
        </div>
      )}
      <ConsultNowSound />
    </div>
  );
};
